import { useState, useEffect, lazy, Suspense } from 'react';
import { Menu, X, Search, ShoppingCart, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, NavLink, useLocation } from 'react-router-dom';
import ThemeToggle from '../ui/ThemeToggle';
import { useCart } from '../../context/CartContext';
import logoEtcs from '../../assets/logo_etcs.png';

// Lazy loading des modals pour alléger le bundle principal
const CartDrawer = lazy(() => import('../ui/CartDrawer'));
const SearchModal = lazy(() => import('../ui/SearchModal'));

const navLinks = [
  { name: 'Accueil', href: '/' },
  {
    name: 'Nos Services', href: '/services',
    children: [
      { name: 'Réseaux & Télécom', href: '/reseaux' },
      { name: 'Tous les services', href: '/services' },
    ]
  },
  { name: 'Boutique', href: '/boutique' },
  { name: 'À Propos', href: '/a-propos' },
  { name: 'Contact', href: '/contact' },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  
  const location = useLocation();
  const { totalItems } = useCart();

  // Close menus on navigation
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setIsSearchOpen(false);
    setIsCartOpen(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [location.pathname]);

  return (
    <>
      <nav
        className="fixed top-0 left-0 w-full z-50 theme-nav border-b py-3 shadow-md"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 flex justify-between items-center">

          {/* Logo */}
          <Link to="/" className="flex items-center -ml-2 sm:ml-0">
            <img
              src={logoEtcs}
              alt="ETCS - Entre Techno Com Services"
              className="h-28 sm:h-36 md:h-44 w-auto object-contain -my-8 sm:-my-12 md:-my-16"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <div key={link.name} className="relative"
                onMouseEnter={() => link.children && setOpenDropdown(link.name)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <NavLink to={link.href}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={({ isActive }) => ({ color: isActive ? 'var(--accent-green)' : 'var(--text-secondary)' })}
                >
                  {link.name}
                  {link.children && <ChevronDown size={14} className={`transition-transform ${openDropdown === link.name ? 'rotate-180' : ''}`} />}
                </NavLink>
                <AnimatePresence>
                  {link.children && openDropdown === link.name && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-1 w-52 rounded-xl overflow-hidden shadow-2xl"
                      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
                    >
                      {link.children.map((child) => (
                        <NavLink key={child.name} to={child.href}
                          className="block px-4 py-3 text-sm transition-colors hover:opacity-80"
                          style={({ isActive }) => ({
                            color: isActive ? 'var(--accent-green)' : 'var(--text-secondary)',
                            background: isActive ? 'rgba(63, 120, 43, 0.05)' : 'transparent',
                            borderBottom: '1px solid var(--border)'
                          })}
                        >
                          {child.name}
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Search Toggle */}
            <button 
              onClick={() => setIsSearchOpen(true)}
              aria-label="Ouvrir la recherche"
              className="p-2 rounded-lg transition-colors hover:bg-white/5" 
              style={{ color: 'var(--text-secondary)' }}
            >
              <Search size={20} />
            </button>

            {/* Cart Toggle */}
            <button 
              onClick={() => setIsCartOpen(true)}
              aria-label={`Ouvrir le panier (${totalItems} articles)`}
              className="flex p-2 rounded-lg transition-colors relative hover:bg-white/5" 
              style={{ color: 'var(--text-secondary)' }}
            >
              <ShoppingCart size={20} />
              {totalItems > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 text-black text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--accent-green)' }}
                >
                  {totalItems}
                </motion.span>
              )}
            </button>

            {/* Menu Hamburger */}
            <button 
              className="p-2 rounded-lg transition-colors hover:bg-white/5" 
              style={{ color: 'var(--text-secondary)' }} 
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Ouvrir le menu de navigation"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Search Modal Premium (Chargé à la demande) */}
      <Suspense fallback={null}>
        {isSearchOpen && <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />}
      </Suspense>

      {/* Side Menu (Hamburger Menu) / Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[60] backdrop-blur-md bg-black/40"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Sidebar Panel */}
            <motion.div 
              initial={{ x: '100%' }} 
              animate={{ x: 0 }} 
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 30, stiffness: 200, mass: 1 }}
              style={{ background: 'var(--bg)', borderLeft: '1px solid var(--border)', willChange: 'transform' }}
              className="fixed top-0 right-0 h-screen w-full sm:w-[400px] z-[70] flex flex-col shadow-2xl"
            >
              {/* BLOCK 1: HEADER */}
              <div className="flex justify-between items-center px-8 py-6" style={{ borderBottom: '1px solid var(--border)' }}>
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center -ml-4">
                  <img 
                    src={logoEtcs} 
                    alt="ETCS" 
                    className="h-32 sm:h-36 md:h-44 w-auto object-contain -my-10 sm:-my-12 md:-my-16" 
                  />
                </Link>
                <button 
                  className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors" 
                  style={{ color: 'var(--text-primary)' }} 
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Fermer le menu"
                >
                  <X size={32} />
                </button>
              </div>
              
              {/* BLOCK 2: NAVIGATION (Center) */}
              <div className="flex-1 overflow-y-auto px-10 py-12">
                <div className="flex flex-col space-y-8 text-left">
                  {navLinks.map((link, i) => (
                    <div key={link.name} className="flex flex-col">
                      <motion.div 
                        initial={{ x: 30, opacity: 0 }} 
                        animate={{ x: 0, opacity: 1 }} 
                        transition={{ delay: 0.1 + i * 0.05 }}
                      >
                        <Link 
                          to={link.href} 
                          onClick={() => setMobileMenuOpen(false)} 
                          className="text-3xl font-extrabold tracking-tight hover:text-[#3f782b] transition-colors"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {link.name}
                        </Link>
                      </motion.div>
                      
                      {link.children && (
                        <div className="mt-4 flex flex-col space-y-4 border-l-2 ml-1 pl-6" style={{ borderColor: 'var(--border)' }}>
                          {link.children.map((child, childIdx) => (
                            <motion.div
                              key={child.name}
                              initial={{ x: 20, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: 0.2 + i * 0.05 + childIdx * 0.03 }}
                            >
                              <Link 
                                to={child.href} 
                                onClick={() => setMobileMenuOpen(false)} 
                                className="text-lg font-medium hover:text-[#3f782b] transition-colors" 
                                style={{ color: 'var(--text-secondary)' }}
                              >
                                {child.name}
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* BLOCK 3: ACTIONS (Bottom) */}
              <div className="px-8 pb-12 pt-8 space-y-6" style={{ borderTop: '1px solid var(--border)' }}>
                {/* Theme Toggle integrated */}
                <motion.div 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  transition={{ delay: 0.4 }}
                  className="flex items-center justify-between bg-black/5 dark:bg-white/5 p-4 rounded-2xl border"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <span className="text-base font-bold" style={{ color: 'var(--text-secondary)' }}>Mode d'affichage</span>
                  <ThemeToggle />
                </motion.div>

                <div className="space-y-4">
                  <button 
                    onClick={() => { setMobileMenuOpen(false); setIsCartOpen(true); }}
                    className="flex items-center justify-center gap-3 w-full py-4 rounded-2xl font-bold text-lg transition-all hover:bg-white/5 border border-white/10 active:scale-95"
                    style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', borderColor: 'var(--border)' }}
                  >
                    <ShoppingCart size={20} />
                    Mon Panier ({totalItems})
                  </button>
                  
                  <Link 
                    to="/contact" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="flex items-center justify-center w-full text-black font-black py-5 rounded-2xl text-xl shadow-xl shadow-[#3f782b]/20 transition-all hover:scale-[1.02] active:scale-95" 
                    style={{ background: 'var(--accent-green)' }}
                  >
                    Demander un Devis
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Suspense fallback={null}>
        <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      </Suspense>
    </>
  );
};

export default Navbar;
